import { useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { BoatNameDropdown } from 'components/custom/BoatNameDropdown'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { useMutation } from '@apollo/client'
import {
    CHANGE_BOAT_ROUTE,
    CHANGE_BOAT_ROUTE_CAPTAIN,
    ADD_BOAT_ROUTE_ADDITIONAL_CAPTAIN,
    ADD_ROUTE_LUGGAGE_BOAT,
} from 'graphql/boat/mutations'
import { RESCHEDULE_BOAT_ROUTE_TRIP } from 'graphql/operations/mutations'
import { customDateTimeFormat, utcDateTimeToBahrainTime } from 'utils/common'
import moment from 'moment'
import { UserListDropdown } from 'components/custom/userListDropdown'

// Assign Boat Dialog
export function AssignBoatDialog(props) {
    const { t } = useTranslation()
    const { status, title, onClose, onSuccess, historyParams } = props
    const [open, setOpen] = useState(status)
    const [newBoatId, setNewBoatId] = useState(null)
    const [showBoatUpdateConfirm, setShowBoatUpdateConfirm] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Update '))
    const [submitLoading, setSubmitLoading] = useState(false)

    const handleOverlayClick = event => {
        event.stopPropagation() // Prevent the event from bubbling up
    }

    // Mutation change New Boat
    const [assignNewBoat] = useMutation(CHANGE_BOAT_ROUTE, {
        onCompleted: data => {
            if (data.changeRouteBoat.code === '200' && data.changeRouteBoat.success) {
                setOpen(false)
                setSubmitLoading(false)
                onSuccess()
            } else {
                setSubmitLoading(false)
                setAddError(data.changeRouteBoat.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleBoatUpdateCancel = () => {
        setShowBoatUpdateConfirm(false)
    }
    const handleBoatUpdateConfirm = () => {
        console.log('Boat Update Confirm', newBoatId)
        setSubmitLoading(true)
        assignNewBoat({
            variables: {
                historyId: historyParams?.historyId,
                boatId: newBoatId || historyParams?.boatId,
                activityDate: historyParams?.date,
            },
        })
    }
    const handleDialogClose = () => {
        setShowErrDialog(false)
    }

    return (
        <Dialog open={open} onClose={onClose} onOverlayClick={handleOverlayClick} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="">
                                {/* <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <ExclamationTriangleIcon aria-hidden="true" className="h-12 w-12 text-error" />
                                </div> */}
                                <div className="">
                                    {/* <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-error">
                                        {title}
                                    </DialogTitle> */}
                                    {!showBoatUpdateConfirm && (
                                        <>
                                            <div className="bg-white px-4 pb-4 pt-5 sm:p-0 sm:pb-4">
                                                <div className="px-4 sm:px-0">
                                                    <h3 className="text-lg font-bold leading-7 text-gray-900 text-primary ps-2">
                                                        {title}
                                                    </h3>
                                                </div>
                                                <div className="mt-6 border-t border-gray-100">
                                                    <dl className="divide-y divide-gray-100">
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Date')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {historyParams?.date}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Captain Name')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {historyParams?.captainName}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Start Time')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {customDateTimeFormat(historyParams.startTime)}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('End Time')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {customDateTimeFormat(historyParams?.endTime)}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Current Boat')}
                                                            </dt>
                                                            <dd className="mt-1 leading-6 text-gray-700 text-sm font-bold sm:col-span-2 sm:mt-0 ">
                                                                {historyParams?.boatName || (
                                                                    <span className="italic text-gray-500 font-[400]">
                                                                        {t('No Boat Assigned')}
                                                                    </span>
                                                                )}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50 items-center">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Change Boat')}
                                                            </dt>
                                                            <dd className="mt-1 leading-6 text-gray-700 text-md font-bold sm:col-span-2 sm:mt-0 ">
                                                                <BoatNameDropdown
                                                                    fields={{
                                                                        name: 'boatId',
                                                                        id: 'boatId',
                                                                        value: newBoatId || historyParams?.boatId || '',
                                                                        onChange: e => {
                                                                            console.log('onChange', e.target.value)
                                                                            setNewBoatId(e.target.value)
                                                                        },
                                                                    }}
                                                                    purpose="add"
                                                                    statusCode="ACT"
                                                                />
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        {!showBoatUpdateConfirm && (
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        data-autofocus
                                        onClick={onClose}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button
                                        disabled={!newBoatId}
                                        type="button"
                                        data-autofocus
                                        onClick={() => {
                                            setShowBoatUpdateConfirm(true)
                                        }}
                                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {showBoatUpdateConfirm && (
                            <>
                                <div className="bg-white px-4 pb-4 pt-0 sm:pl-6 pt-0 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div
                                            className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 bg-red-800"
                                            style={{ backgroundColor: '#ff00002e' }}
                                        >
                                            <ExclamationTriangleIcon
                                                aria-hidden="true"
                                                className="h-6 w-6 text-red-600 text-error"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left pb-3">
                                            <DialogTitle as="h3" className="text-md font-bold leading-6 text-gray-900">
                                                Are you sure?
                                            </DialogTitle>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to update the boat?
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleBoatUpdateCancel}
                                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            {t('No')}
                                        </button>
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleBoatUpdateConfirm}
                                            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                        >
                                            {t('Yes')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {showErrDialog && (
                            <ErrorDialog status={true} title="Error" message={addError} onClose={handleDialogClose} />
                        )}
                        {/* Overlay and Loading Spinner */}
                        {submitLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}
// Assign Luggage Boat Dialog
export function AssignLuggageBoatDialog(props) {
    const { t } = useTranslation()
    const { status, title, onClose, onSuccess, historyParams } = props
    const [open, setOpen] = useState(status)
    const [newBoatId, setNewBoatId] = useState(null)
    const [showBoatUpdateConfirm, setShowBoatUpdateConfirm] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Update '))
    const [submitLoading, setSubmitLoading] = useState(false)

    const handleOverlayClick = event => {
        event.stopPropagation() // Prevent the event from bubbling up
    }

    // Mutation change New Luggage Boat
    const [assignNewBoat] = useMutation(ADD_ROUTE_LUGGAGE_BOAT, {
        onCompleted: data => {
            if (data.addRouteLuggageBoat.code === '200' && data.addRouteLuggageBoat.success) {
                setOpen(false)
                setSubmitLoading(false)
                onSuccess()
            } else {
                setSubmitLoading(false)
                setAddError(data.addRouteLuggageBoat.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleBoatUpdateCancel = () => {
        setShowBoatUpdateConfirm(false)
    }
    const handleBoatUpdateConfirm = () => {
        console.log('Luggage Boat Update Confirm', newBoatId)
        setSubmitLoading(true)
        assignNewBoat({
            variables: {
                history_id: historyParams?.historyId,
                luggage_boat_ids: [newBoatId] || [historyParams?.luggage_boats[0].id],
                activity_date: historyParams?.date,
            },
        })
    }
    const handleDialogClose = () => {
        setShowErrDialog(false)
    }

    return (
        <Dialog open={open} onClose={onClose} onOverlayClick={handleOverlayClick} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            {!showBoatUpdateConfirm && (
                                <>
                                    <div className="bg-white px-4 pb-4 pt-5 sm:p-0 sm:pb-4">
                                        <div className="px-4 sm:px-0">
                                            <h3 className="text-lg font-bold leading-7 text-gray-900 text-primary ps-2">
                                                {title}
                                            </h3>
                                        </div>
                                        <div className="mt-6 border-t border-gray-100">
                                            <dl className="divide-y divide-gray-100">
                                                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Date')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {historyParams?.date}
                                                    </dd>
                                                </div>
                                                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Start Time')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {customDateTimeFormat(historyParams.startTime)}
                                                    </dd>
                                                </div>
                                                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('End Time')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {customDateTimeFormat(historyParams?.endTime)}
                                                    </dd>
                                                </div>
                                                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Current Luggage Boat')}
                                                    </dt>
                                                    <dd className="mt-1 leading-6 text-gray-700 text-sm font-bold sm:col-span-2 sm:mt-0 ">
                                                        {historyParams?.luggage_boats[0]?.name?.en || (
                                                            <span className="italic text-gray-500 font-[400]">
                                                                {t('No Boat Assigned')}
                                                            </span>
                                                        )}
                                                    </dd>
                                                </div>
                                                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50 items-center">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Change Boat')}
                                                    </dt>
                                                    <dd className="mt-1 leading-6 text-gray-700 text-md font-bold sm:col-span-2 sm:mt-0 ">
                                                        <BoatNameDropdown
                                                            fields={{
                                                                name: 'boatId',
                                                                id: 'boatId',
                                                                value:
                                                                    newBoatId ||
                                                                    historyParams?.luggage_boats[0]?.id ||
                                                                    '',
                                                                onChange: e => {
                                                                    console.log('onChange', e.target.value)
                                                                    setNewBoatId(e.target.value)
                                                                },
                                                            }}
                                                            purpose="add"
                                                            statusCode="ACT"
                                                        />
                                                    </dd>
                                                </div>
                                            </dl>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {!showBoatUpdateConfirm && (
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        data-autofocus
                                        onClick={onClose}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button
                                        disabled={!newBoatId}
                                        type="button"
                                        data-autofocus
                                        onClick={() => {
                                            setShowBoatUpdateConfirm(true)
                                        }}
                                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {showBoatUpdateConfirm && (
                            <>
                                <div className="bg-white px-4 pb-4 pt-0 sm:pl-6 pt-0 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div
                                            className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 bg-red-800"
                                            style={{ backgroundColor: '#ff00002e' }}
                                        >
                                            <ExclamationTriangleIcon
                                                aria-hidden="true"
                                                className="h-6 w-6 text-red-600 text-error"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left pb-3">
                                            <DialogTitle as="h3" className="text-md font-bold leading-6 text-gray-900">
                                                Are you sure?
                                            </DialogTitle>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to update the boat?
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleBoatUpdateCancel}
                                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            {t('No')}
                                        </button>
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleBoatUpdateConfirm}
                                            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                        >
                                            {t('Yes')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {showErrDialog && (
                            <ErrorDialog status={true} title="Error" message={addError} onClose={handleDialogClose} />
                        )}
                        {/* Overlay and Loading Spinner */}
                        {submitLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}
// Assign Captain Dialog
export function AssignCaptainDialog(props) {
    const { t } = useTranslation()
    const { status, title, onClose, onSuccess, historyParams } = props
    const [open, setOpen] = useState(status)
    const [newCaptainId, setNewCaptainId] = useState(null)
    const [showCaptainUpdateConfirm, setShowCaptainUpdateConfirm] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Update'))
    const [submitLoading, setSubmitLoading] = useState(false)

    const handleOverlayClick = event => {
        event.stopPropagation() // Prevent the event from bubbling up
    }

    // Mutation change New Boat
    const [assignNewCaptain] = useMutation(CHANGE_BOAT_ROUTE_CAPTAIN, {
        onCompleted: data => {
            if (data.changeBoatRouteCaptain.code === '200' && data.changeBoatRouteCaptain.success) {
                setOpen(false)
                setSubmitLoading(false)
                onSuccess()
            } else {
                setSubmitLoading(false)
                setAddError(data.changeBoatRouteCaptain.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleBoatUpdateCancel = () => {
        setShowCaptainUpdateConfirm(false)
    }

    const handleCaptainUpdateConfirm = () => {
        console.log('Captain Update Confirm', newCaptainId)
        setSubmitLoading(true)
        assignNewCaptain({
            variables: {
                historyId: historyParams?.historyId,
                captainId: newCaptainId || historyParams?.captainId,
                activityDate: historyParams?.date,
            },
        })
    }
    const handleDialogClose = () => {
        setShowErrDialog(false)
    }

    return (
        <Dialog open={open} onClose={onClose} onOverlayClick={handleOverlayClick} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="">
                                {/* <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <ExclamationTriangleIcon aria-hidden="true" className="h-12 w-12 text-error" />
                                </div> */}
                                <div className="">
                                    {/* <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-error">
                                        {title}
                                    </DialogTitle> */}
                                    {!showCaptainUpdateConfirm && (
                                        <div className="bg-white px-4 pb-4 pt-5 sm:p-0 sm:pb-4">
                                            <div className="px-4 sm:px-0">
                                                <h3 className="text-lg font-bold leading-7 text-gray-900 text-primary ps-2">
                                                    {title}
                                                </h3>
                                            </div>
                                            <div className="mt-6 border-t border-gray-100">
                                                <dl className="divide-y divide-gray-100">
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Date')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {historyParams?.date}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Boat Name')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {historyParams?.boatName}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Start Time')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {customDateTimeFormat(historyParams?.startTime)}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('End Time')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {customDateTimeFormat(historyParams?.endTime)}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Current Captain')}
                                                        </dt>
                                                        <dd className="mt-1 leading-6 text-gray-700 text-sm font-bold sm:col-span-2 sm:mt-0 ">
                                                            {historyParams?.captainName || (
                                                                <span className="italic text-gray-500 font-[400]">
                                                                    {t('No Captain Assigned')}
                                                                </span>
                                                            )}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50 items-center">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Change Captain')}
                                                        </dt>
                                                        <dd className="mt-1 leading-6 text-gray-700 text-md font-bold sm:col-span-2 sm:mt-0 ">
                                                            <UserListDropdown
                                                                userType="captain"
                                                                fields={{
                                                                    name: 'captainId',
                                                                    id: 'captainId',
                                                                    value:
                                                                        newCaptainId || historyParams?.captainId || '',
                                                                    onChange: e => {
                                                                        console.log('onChange', e.target.value)
                                                                        setNewCaptainId(e.target.value)
                                                                    },
                                                                }}
                                                                purpose="add"
                                                                isActive={true}
                                                            />
                                                        </dd>
                                                    </div>
                                                </dl>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {!showCaptainUpdateConfirm && (
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        data-autofocus
                                        onClick={onClose}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        data-autofocus
                                        disabled={!newCaptainId}
                                        onClick={() => {
                                            setShowCaptainUpdateConfirm(true)
                                        }}
                                        className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                                            newCaptainId
                                                ? 'bg-primary hover:bg-primary/[0.9]'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {showCaptainUpdateConfirm && (
                            <>
                                <div className="bg-white px-4 pb-4 pt-0 sm:pl-6 pt-0 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div
                                            className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 bg-red-800"
                                            style={{ backgroundColor: '#ff00002e' }}
                                        >
                                            <ExclamationTriangleIcon
                                                aria-hidden="true"
                                                className="h-6 w-6 text-red-600 text-error"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left pb-3">
                                            <DialogTitle as="h3" className="text-md font-bold leading-6 text-gray-900">
                                                {t('Are you sure?')}
                                            </DialogTitle>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    {t('Are you sure you want to update the Captain?')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleBoatUpdateCancel}
                                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            {t('No')}
                                        </button>
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleCaptainUpdateConfirm}
                                            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                        >
                                            {t('Yes')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {showErrDialog && (
                            <ErrorDialog status={true} title="Error" message={addError} onClose={handleDialogClose} />
                        )}

                        {/* Overlay and Loading Spinner */}
                        {submitLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}

// Assign Additional Captain Dialog
export function AssignAdditionalCaptainDialog(props) {
    const { t } = useTranslation()
    const { status, title, onClose, onSuccess, historyParams } = props
    const [open, setOpen] = useState(status)
    const [newAdditionalCaptainId, setNewAdditionalCaptainId] = useState(null)
    const [showAdditionalCaptainUpdateConfirm, setShowAdditionalCaptainUpdateConfirm] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Update'))
    const [submitLoading, setSubmitLoading] = useState(false)

    const handleOverlayClick = event => {
        event.stopPropagation() // Prevent the event from bubbling up
    }

    // Mutation change New Additional Captain
    const [assignNewAdditionalCaptain] = useMutation(ADD_BOAT_ROUTE_ADDITIONAL_CAPTAIN, {
        onCompleted: data => {
            if (data.addBoatRouteAdditionalCaptain.code === '200' && data.addBoatRouteAdditionalCaptain.success) {
                setOpen(false)
                setSubmitLoading(false)
                onSuccess()
            } else {
                setSubmitLoading(false)
                setAddError(data.addBoatRouteAdditionalCaptain.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleAdditionalCaptainUpdateCancel = () => {
        setShowAdditionalCaptainUpdateConfirm(false)
    }

    const handleAdditionalCaptainUpdateConfirm = () => {
        console.log('Additional Captain Update Confirm', newAdditionalCaptainId)
        setSubmitLoading(true)
        assignNewAdditionalCaptain({
            variables: {
                historyId: historyParams?.historyId,
                captainId: newAdditionalCaptainId || historyParams?.additionalCaptainId,
                activityDate: historyParams?.date,
                // Preserve existing seamen assignments
                seamanOneId: historyParams?.seamanOneId || null,
                seamanTwoId: historyParams?.seamanTwoId || null,
            },
        })
    }
    const handleDialogClose = () => {
        setShowErrDialog(false)
    }

    return (
        <Dialog open={open} onClose={onClose} onOverlayClick={handleOverlayClick} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="">
                                <div className="">
                                    {!showAdditionalCaptainUpdateConfirm && (
                                        <div className="bg-white px-4 pb-4 pt-5 sm:p-0 sm:pb-4">
                                            <div className="px-4 sm:px-0">
                                                <h3 className="text-lg font-bold leading-7 text-gray-900 text-primary ps-2">
                                                    {title}
                                                </h3>
                                            </div>
                                            <div className="mt-6 border-t border-gray-100">
                                                <dl className="divide-y divide-gray-100">
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Date')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {historyParams?.date}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Boat Name')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {historyParams?.boatName}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Start Time')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {customDateTimeFormat(historyParams?.startTime)}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('End Time')}
                                                        </dt>
                                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                            {customDateTimeFormat(historyParams?.endTime)}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Current Captain')}
                                                        </dt>
                                                        <dd className="mt-1 leading-6 text-gray-700 text-sm font-bold sm:col-span-2 sm:mt-0 ">
                                                            {historyParams?.captainName || (
                                                                <span className="italic text-gray-500 font-[400]">
                                                                    {t('No Captain Assigned')}
                                                                </span>
                                                            )}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Current Additional Captain')}
                                                        </dt>
                                                        <dd className="mt-1 leading-6 text-gray-700 text-sm font-bold sm:col-span-2 sm:mt-0 ">
                                                            {historyParams?.additionalCaptainName || (
                                                                <span className="italic text-gray-500 font-[400]">
                                                                    {t('No Additional Captain Assigned')}
                                                                </span>
                                                            )}
                                                        </dd>
                                                    </div>
                                                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50 items-center">
                                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                                            {t('Add Additional Captain')}
                                                        </dt>
                                                        <dd className="mt-1 leading-6 text-gray-700 text-md font-bold sm:col-span-2 sm:mt-0 ">
                                                            <UserListDropdown
                                                                userType="captain"
                                                                fields={{
                                                                    name: 'additionalCaptainId',
                                                                    id: 'additionalCaptainId',
                                                                    value:
                                                                        newAdditionalCaptainId ||
                                                                        historyParams?.additionalCaptainId ||
                                                                        '',
                                                                    onChange: e => {
                                                                        console.log('onChange', e.target.value)
                                                                        setNewAdditionalCaptainId(e.target.value)
                                                                    },
                                                                }}
                                                                purpose="add"
                                                                isActive={true}
                                                            />
                                                        </dd>
                                                    </div>
                                                </dl>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {!showAdditionalCaptainUpdateConfirm && (
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        data-autofocus
                                        onClick={onClose}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        data-autofocus
                                        disabled={!newAdditionalCaptainId}
                                        onClick={() => {
                                            setShowAdditionalCaptainUpdateConfirm(true)
                                        }}
                                        className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                                            newAdditionalCaptainId
                                                ? 'bg-primary hover:bg-primary/[0.9]'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {showAdditionalCaptainUpdateConfirm && (
                            <>
                                <div className="bg-white px-4 pb-4 pt-0 sm:pl-6 pt-0 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div
                                            className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 bg-red-800"
                                            style={{ backgroundColor: '#ff00002e' }}
                                        >
                                            <ExclamationTriangleIcon
                                                aria-hidden="true"
                                                className="h-6 w-6 text-red-600 text-error"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left pb-3">
                                            <DialogTitle as="h3" className="text-md font-bold leading-6 text-gray-900">
                                                Are you sure?
                                            </DialogTitle>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to add additional captain ?
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleAdditionalCaptainUpdateCancel}
                                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            {t('No')}
                                        </button>
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleAdditionalCaptainUpdateConfirm}
                                            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                        >
                                            {t('Yes')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {showErrDialog && (
                            <ErrorDialog status={true} title="Error" message={addError} onClose={handleDialogClose} />
                        )}

                        {/* Overlay and Loading Spinner */}
                        {submitLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}

// Reschedule Trip Dialog
export function RescheduleDialog(props) {
    const { t } = useTranslation()
    const { status, title, onClose, onSuccess, historyParams } = props
    const [open, setOpen] = useState(status)
    const [reason, setReason] = useState('')
    const [rescheduledStart, setRescheduledStart] = useState('')
    const [showRescheduleConfirm, setShowRescheduleConfirm] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Rescheduling'))
    const [submitLoading, setSubmitLoading] = useState(false)

    const handleOverlayClick = event => {
        event.stopPropagation()
    }

    // Mutation to reschedule trip
    const [rescheduleTrip] = useMutation(RESCHEDULE_BOAT_ROUTE_TRIP, {
        onCompleted: data => {
            if (data.rescheduleBoatRouteTrip.code === '200' && data.rescheduleBoatRouteTrip.success) {
                setOpen(false)
                setSubmitLoading(false)
                onSuccess()
            } else {
                setSubmitLoading(false)
                setAddError(data.rescheduleBoatRouteTrip.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleRescheduleCancel = () => {
        setShowRescheduleConfirm(false)
    }

    const handleRescheduleConfirm = () => {
        console.log('Reschedule Confirm', { reason, rescheduledStart })
        setSubmitLoading(true)

        const newDateTime = moment.utc(`${historyParams?.date} ${rescheduledStart}`, 'YYYY-MM-DD hh:mm A').toISOString()

        console.log(historyParams)
        const tripId = historyParams?.trip?.trip_id || historyParams?.tripId

        rescheduleTrip({
            variables: {
                tripId: tripId,
                tripDate: historyParams?.date,
                reschedule: {
                    reason: reason || null,
                    rescheduled_start: newDateTime,
                },
            },
        })
    }

    const handleDialogClose = () => {
        setShowErrDialog(false)
    }

    // Extract time from ISO datetime for initial display
    const currentTime = historyParams?.startTime
        ? utcDateTimeToBahrainTime(historyParams.startTime) // uses your helper
        : ''

    // Validation: Check if new time is provided
    const isValid = rescheduledStart && rescheduledStart.trim() !== ''

    return (
        <Dialog open={open} onClose={onClose} onOverlayClick={handleOverlayClick} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="">
                                <div className="">
                                    {!showRescheduleConfirm && (
                                        <>
                                            <div className="bg-white px-4 pt-5 sm:p-0 ">
                                                <div className="px-4 sm:px-0">
                                                    <h3 className="text-lg font-bold leading-7 text-gray-900 text-primary ps-2">
                                                        {title}
                                                    </h3>
                                                </div>
                                                <div className="mt-2 border-t border-gray-100">
                                                    <dl className="divide-y divide-gray-100">
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Date')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {historyParams?.date}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Route')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {historyParams?.routeName}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Trip')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {historyParams?.tripName}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Boat Name')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {historyParams?.boatName}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Captain Name')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {historyParams?.captainName}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Current Start Time')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {utcDateTimeToBahrainTime(historyParams.startTime)}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Current End Time')}
                                                            </dt>
                                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                                {utcDateTimeToBahrainTime(historyParams?.endTime)}
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('New Start Time')}{' '}
                                                                <span className="text-red-500">*</span>
                                                            </dt>
                                                            <dd className="mt-1 leading-6 text-gray-700 text-md font-bold sm:col-span-2 sm:mt-0">
                                                                <input
                                                                    type="time"
                                                                    value={rescheduledStart}
                                                                    onChange={e => setRescheduledStart(e.target.value)}
                                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                                    required
                                                                />
                                                                <p className="mt-1 text-xs text-gray-500">
                                                                    {t('Current')}: {currentTime}
                                                                </p>
                                                            </dd>
                                                        </div>
                                                        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3 bg-slate-50">
                                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                                {t('Reason')}
                                                            </dt>
                                                            <dd className="mt-1 leading-6 text-gray-700 text-md sm:col-span-2 sm:mt-0">
                                                                <textarea
                                                                    rows={3}
                                                                    value={reason}
                                                                    onChange={e => setReason(e.target.value)}
                                                                    placeholder={t(
                                                                        'Enter reason for rescheduling (optional)'
                                                                    )}
                                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                                />
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        {!showRescheduleConfirm && (
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        data-autofocus
                                        onClick={onClose}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button
                                        disabled={!isValid}
                                        type="button"
                                        data-autofocus
                                        onClick={() => {
                                            setShowRescheduleConfirm(true)
                                        }}
                                        className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                                            isValid
                                                ? 'bg-primary hover:bg-primary/[0.9]'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {showRescheduleConfirm && (
                            <>
                                <div className="bg-white px-4 pb-4 pt-0 sm:pl-6 pt-0 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div
                                            className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 bg-red-800"
                                            style={{ backgroundColor: '#ff00002e' }}
                                        >
                                            <ExclamationTriangleIcon
                                                aria-hidden="true"
                                                className="h-6 w-6 text-red-600 text-error"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left pb-3">
                                            <DialogTitle as="h3" className="text-md font-bold leading-6 text-gray-900">
                                                {t('Are you sure?')}
                                            </DialogTitle>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    {t('Are you sure you want to reschedule this trip?')}
                                                </p>
                                                <p className="text-sm text-gray-700 mt-2">
                                                    <strong>{t('New Start Time')}:</strong> {rescheduledStart}
                                                </p>
                                                {reason && (
                                                    <p className="text-sm text-gray-700 mt-1">
                                                        <strong>{t('Reason')}:</strong> {reason}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleRescheduleCancel}
                                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            {t('No')}
                                        </button>
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={handleRescheduleConfirm}
                                            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                        >
                                            {t('Yes')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {showErrDialog && (
                            <ErrorDialog status={true} title="Error" message={addError} onClose={handleDialogClose} />
                        )}
                        {/* Overlay and Loading Spinner */}
                        {submitLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}
